import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserProfile } from '@/shared/types';
import { Search, X } from 'lucide-react';
import { UserActionDialog, ActionType } from '@/features/admin/components/users/UserActionDialog';
import { UserPreviewModal } from '@/features/admin/components/users/UserPreviewModal';
import { UserTable } from '@/features/admin/components/users/UserTable';
import { BulkActionBar } from '@/features/admin/components/users/BulkActionBar';
import { UserFilters } from '@/features/admin/components/UserFilters';
import { Pagination } from '@/features/admin/components/Pagination';
import { useAdminUsers } from '@/features/admin/hooks/useAdminUsers';
import AdminUsersSkeleton from '@/features/admin/components/AdminUsersSkeleton';

interface AdminUsersViewProps {
  users?: UserProfile[];
  onRefresh?: () => void;
}

const AdminUsersView: React.FC<AdminUsersViewProps> = ({ users: initialUsers, onRefresh }) => {
  const navigate = useNavigate();
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);

  const handleViewDetails = (userId: string) => {
    navigate(`/admin/users/${userId}`);
  };

  const {
    users,
    loading,
    searchTerm,
    filteredUsers,
    filters,
    sortField,
    sortDirection,
    pagination,
    totalPages,
    totalUsers,
    setSearchTerm,
    setRoleFilter,
    setPlanFilter,
    handleSort,
    initiateAction,
    closeAction,
    actionType,
    actionUser,
    tempPassword,
    newPlanId,
    billingPeriod,
    setNewPlanId,
    setBillingPeriod,
    executeAction,
    nextPage,
    prevPage,
    goToPage,
    // Bulk actions
    selectedUserIds,
    bulkActionType,
    toggleUserSelection,
    toggleSelectAll,
    clearSelection,
    isAllSelected,
    isSomeSelected,
    initiateBulkAction,
    closeBulkAction,
    executeBulkAction,
  } = useAdminUsers(initialUsers);

  if (loading) {
    return <AdminUsersSkeleton />;
  }

  return (
    <div className="space-y-5 max-w-6xl mx-auto">
      {/* Header Bar */}
      <div className="flex flex-col gap-4 bg-white p-4 rounded border border-gray-200">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">User Management</h2>
            <p className="text-sm text-gray-500">Manage users, plans, and access</p>
          </div>
          <div className="relative w-full md:w-auto group">
            <Search className="absolute left-3 top-2.5 text-gray-400 group-focus-within:text-gray-600 transition-colors" size={16} />
            <input
              type="text"
              placeholder="Search users..."
              className="pl-9 pr-8 py-2 bg-white border border-gray-200 rounded focus:border-gray-400 focus:outline-none w-full md:w-64 transition-all text-sm"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <UserFilters
          roleFilter={filters.role}
          planFilter={filters.plan}
          onRoleChange={setRoleFilter}
          onPlanChange={setPlanFilter}
        />

        {/* Results count */}
        <div className="text-sm text-gray-500">
          {totalUsers === 0 ? (
            <span>No users found</span>
          ) : (
            <span>
              Showing <span className="font-semibold text-gray-900">{totalUsers}</span> user{totalUsers !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedUserIds.size > 0 && (
        <BulkActionBar
          selectedCount={selectedUserIds.size}
          onBulkDelete={() => initiateBulkAction('delete')}
          onBulkPlanChange={() => initiateBulkAction('change_plan')}
          onClearSelection={clearSelection}
          newPlanId={newPlanId}
          billingPeriod={billingPeriod}
          onNewPlanIdChange={setNewPlanId}
          onBillingPeriodChange={setBillingPeriod}
          onConfirmBulkAction={executeBulkAction}
          onCancelBulkAction={closeBulkAction}
          bulkActionType={bulkActionType}
        />
      )}

      {/* Users Table */}
      <div className="bg-white rounded border border-gray-200 overflow-hidden">
        <UserTable
          users={filteredUsers}
          onPreviewUser={setSelectedUser}
          onInitiateAction={initiateAction}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
          onViewDetails={handleViewDetails}
          selectedUserIds={selectedUserIds}
          onToggleSelection={toggleUserSelection}
          onToggleSelectAll={toggleSelectAll}
          isAllSelected={isAllSelected}
          isSomeSelected={isSomeSelected}
        />
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={totalPages}
          totalItems={totalUsers}
          itemsPerPage={pagination.perPage}
          onPrevious={prevPage}
          onNext={nextPage}
          onPageChange={goToPage}
        />
      </div>

      {/* Action Dialog */}
      <UserActionDialog
        actionType={actionType}
        actionUser={actionUser}
        tempPassword={tempPassword}
        newPlanId={newPlanId}
        billingPeriod={billingPeriod}
        onNewPlanIdChange={setNewPlanId}
        onBillingPeriodChange={setBillingPeriod}
        onConfirm={executeAction}
        onCancel={closeAction}
      />

      {/* User Preview Modal */}
      {selectedUser && (
        <UserPreviewModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onEditPlan={() => {
            setSelectedUser(null);
            initiateAction('change_plan', selectedUser);
          }}
        />
      )}
    </div>
  );
};

export default AdminUsersView;